import { useEffect, useState } from 'react';
import type { Nullable } from 'corgi-types';

import { executeQuery } from 'common/gql';
import type { ReasonsQuery, ReasonsQueryVariables } from 'common/gql/generated/schema';
import { useGQLLocale } from 'common/hooks/locale';
import type { DispatchState } from 'common/types/React';
import type { StepOptions } from '../gql/mutation/types';
import reasonsQuery from '../gql/query/reasons.gql';
import type { ReasonsContent } from '../gql/query/types';
import type { StepSubmitHandler } from '../types/stepSubmitHandler';
import { dispatchSubmitStepEvent } from '../utils/tracking';
import type { ReasonsTrackingEventData } from '../utils/tracking';
import type { DrawerAlert } from './alert';
import { useFetchStepContent, useObserveAlert } from './index';
import {
  getStepOptionsByItemId,
  getUpdateReclamationVariables,
  updateReclamation,
} from './update-reclamation';

type UseReasonsOptions = {
  reclamationId: string;
  itemId: string;
  alert: DrawerAlert;
};

interface UseReasonAssignerOptions {
  reclamationId: string;
  itemId: string;
  reasonId: Nullable<string>;
  trackingEventData: ReasonsTrackingEventData;
  setIsNextDisabled: (value: boolean) => void;
  setOnSubmit: DispatchState<StepSubmitHandler>;
}

const fetchReasonsStepContent = async (reclamationId: string, itemId: string) => {
  const variables = { reclamationId, itemId };
  const { reclamationItem, content } = await executeQuery<ReasonsQuery, ReasonsQueryVariables>({
    query: reasonsQuery,
    variables,
    useApq: true,
  });

  const reason = reclamationItem?.reason;

  return { currentId: reason?.id ?? null, content };
};

export const useReasons = ({ reclamationId, itemId, alert }: UseReasonsOptions) => {
  const [internalAlert, setInternalAlert] = useState(alert);

  const {
    content: reasonsContent,
    id: reasonId,
    setId: setReasonId,
  } = useFetchStepContent<ReasonsContent>({
    itemId,
    reclamationId,
    fetcher: fetchReasonsStepContent,
    setAlert: setInternalAlert,
  });

  useObserveAlert(alert, setInternalAlert);

  return { reasonId, reasonsContent, internalAlert, setReasonId };
};

export const useReasonAssigner = ({
  reclamationId,
  itemId,
  reasonId,
  trackingEventData,
  setIsNextDisabled,
  setOnSubmit,
}: UseReasonAssignerOptions) => {
  const locale = useGQLLocale();
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!reasonId) {
      setIsNextDisabled(true);

      return;
    }

    let isAlive = true;
    let response: Nullable<StepOptions> = null;

    setIsNextDisabled(false);
    setOnSubmit(() => async () => {
      if (isAlive) {
        setIsFetching(true);
      }

      setIsNextDisabled(true);

      try {
        const { data } = await updateReclamation(
          getUpdateReclamationVariables({
            reclamationId,
            itemId,
            reason: reasonId,
            locale,
          })
        );

        if (data?.items) {
          response = getStepOptionsByItemId(data.items, itemId);
          dispatchSubmitStepEvent({ ...trackingEventData, label: reasonId });
        }
      } finally {
        if (isAlive) {
          setIsFetching(false);
        }
      }

      return response;
    });

    return () => {
      isAlive = false;
    };
  }, [reclamationId, itemId, reasonId, setIsNextDisabled, setOnSubmit]);

  return isFetching;
};
